import React, { useEffect, useState } from "react";
import { omit, isEmpty } from "lodash";
import styled from "styled-components";
import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";
import Modal, {
  MODAL_HEADER_ALIGN_TITLE,
  MODAL_SIZE,
  ModalHeader,
} from "@8x8/oxygen-modal";
import { errorNotification, useErrorNotification } from "@/react/notifications";
import { CustomerCard } from "@/react/features/customer/card";
import TableComponent from "@/react/components/table/TableComponent";
import { useTable } from "@/react/components/table/useTable";
import PageLayout from "@/react/features/styled/PageLayout";
import ContentWrapper from "@/react/features/styled/PageContentContainer";
import TableWrapper from "@/react/features/styled/TableWrapper";
import PageTitle from "@/react/components/PageTitle";
import LabeledSelect from "@/react/components/LabeledSelect";
import LabeledDateSelector from "@/react/components/LabeledDateSelector";
import { useGetServicesQuery, useLazyGetAuditsQuery } from "./auditApi";
import {
  getDateFromOxygenSelector,
  validateAuditDateRange,
} from "./dateRangeValidation";
import AuditDetails from "./AuditDetails";
import schema from "./schema";
import columnDefinition from "./columnDefinition";

const OverflowScrollableDiv = styled.div`
  overflow-y: scroll;
`;

const ServiceAndDateContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

const columns = columnDefinition();

const getStartDateInitialValue = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  date.setHours(0, 0, 0, 0);
  return date;
};

const AuditPage = () => {
  const {
    data: services,
    isFetching: isFetchingServices,
    error,
  } = useGetServicesQuery();

  useErrorNotification(error);

  const [getAudits, { isFetching: isFetchingAudits }] = useLazyGetAuditsQuery();
  const [selectedService, setSelectedService] = useState("");
  const [startDate, setStartDate] = useState(getStartDateInitialValue());
  const [endDate, setEndDate] = useState(new Date().setHours(23, 59, 59, 999));
  const [auditList, setAuditList] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 25,
  });
  const [pageCount, setPageCount] = useState(0);
  const [auditSelected, setAuditSelected] = useState(null);

  const options = services?.map((service) => ({
    label: service.name,
    value: service.name,
  }));

  useEffect(() => {
    if (!isEmpty(selectedService)) {
      const dateValidationResult = validateAuditDateRange(startDate, endDate);
      if (dateValidationResult === true) {
        getAudits({
          startDate: getDateFromOxygenSelector(startDate).toISOString(),
          endDate: getDateFromOxygenSelector(endDate).toISOString(),
          service: selectedService,
          pageSize: pagination.pageSize,
          page: pagination.pageIndex,
        })
          .then((res) => {
            setAuditList(res?.data?.audits || []);
            setPageCount(res?.data?.page.totalPages || 0);
          })
          .catch(errorNotification);
      } else {
        notify(dateValidationResult);
      }
    }
  }, [selectedService, startDate, endDate, pagination]);

  const { table, filters, state, mutations } = useTable(
    auditList,
    columns,
    schema,
    schema.fields.id.spec.key,
    {
      manualPagination: true,
      pageCount,
      onPaginationChange: setPagination,
      autoResetPageIndex: false,
      enableSorting: false,
      enableFilters: false,
    },
    {
      pagination,
    }
  );

  const finalMutations = React.useMemo(
    () => omit(mutations, ["addRows", "deleteRows", "updateRows"]),
    [mutations]
  );

  const onRowClick = (row) => {
    const rowId = row.getValue("id");
    const details = row.getValue("details");
    if (!!details) {
      setAuditSelected({
        id: rowId,
        details: JSON.parse(details),
      });
    } else {
      notify({
        content: `No details available for audit ${rowId}`,
        type: TYPE.info,
        duration: DURATION.INFINITE,
      });
    }
  };

  return (
    <PageLayout>
      {auditSelected && (
        <Modal
          onClose={() => setAuditSelected(null)}
          shouldCloseOnOverlayClick
          shouldCloseOnEsc
          size={MODAL_SIZE.BIG}
          height={"80vh"}
        >
          <ModalHeader
            title={`Audit ${auditSelected.id} - Diff`}
            alignTitle={MODAL_HEADER_ALIGN_TITLE.LEFT}
          />
          <OverflowScrollableDiv>
            <AuditDetails auditDetails={auditSelected.details} />
          </OverflowScrollableDiv>
        </Modal>
      )}
      <CustomerCard />
      <ContentWrapper>
        <PageTitle>Audit Trail</PageTitle>
        <ServiceAndDateContainer>
          <LabeledSelect
            prompt={"Service"}
            placeholder={
              isFetchingServices ? "Loading services..." : "Choose service"
            }
            isDisabled={isFetchingServices}
            onSelectionChanged={(option) => setSelectedService(option)}
            options={options}
            value={selectedService}
            isClearable
          />
          <LabeledDateSelector
            prompt={"Start Date"}
            placeholder={"Start date"}
            onDateTimeChanged={(date) =>
              setStartDate(date.setHours(0, 0, 0, 0))
            }
            value={startDate}
            singleDate
          />
          <LabeledDateSelector
            prompt={"End Date"}
            placeholder={"End date"}
            onDateTimeChanged={(date) =>
              setEndDate(date.setHours(23, 59, 59, 999))
            }
            value={endDate}
            singleDate
          />
        </ServiceAndDateContainer>
        <TableWrapper>
          <TableComponent
            title="Audits"
            schema={schema}
            isLoading={isFetchingAudits}
            table={table}
            filters={filters}
            mutations={finalMutations}
            state={state}
            onRowClick={onRowClick}
          />
        </TableWrapper>
      </ContentWrapper>
    </PageLayout>
  );
};

export default AuditPage;
